import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import ActionComp from "../layouts/ActionComp.jsx";
import BreadcrumbComp from "../layouts/BreadcrumbComp.jsx";
import GridMaster from "./GridMaster.jsx";
import ActionRenderer from "./ActionRenderer.jsx";
import { exportToExcel } from "../utils/ExcelCreation.js";
import ViewItemMaster from "./ViewItemMaster.jsx";
import { useDispatch } from "react-redux";
import { menuActions } from "../../store/menu-slice.js";
import { searchActions } from "../../store/grid-slice.js";
import { sortByPropertyInAscending } from "../../utils/Utility.js";
import { AuthContext } from "../../contexts/AuthContext.js";
import { addLogDetails } from "../../services/logger-entry-service.js";
import { getItems } from "../../services/item-master-service.js";

const ItemMaster = () => {
    const context = useContext(AuthContext);
    const userDetails = { email: context.userEmail, userName: context.userName, role: context.role, userId: context.userId }
    const [rowData, setRowData] = useState({});
    const [filteredResult, setFilteredResult] = useState([]);
    const [action, setAction] = useState("");
    const [reload, setReload] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [resetFilter, setResetFilter] = useState(true);
    const [loading, setLoading] = useState(true);
    const [filteredResulttemp, setFilteredResulttemp] = useState([]);
    const [filterColLength, setFilterColLength] = useState(0);

    const dispatch = useDispatch();
    dispatch(menuActions.setMenu("Item Master"));

    const columnDefs = [
        {
            headerName: "Item Code",
            field: "ItemCode",
            minWidth: 90,
            tooltipField: "ItemCode",
        },
        {
            headerName: "Item Description",
            field: "ItemDescription",
            tooltipField: "ItemDescription",
            minWidth: 260,
        },
        {
            headerName: "Item Type",
            field: "ItemType",
            minWidth: 100,
            tooltipField: "ItemType",
        },
        {
            headerName: "Product Class",
            field: "ProductClass",
            minWidth: 120,
            tooltipField: "ProductClass",
        },
        {
            headerName: "Commodity Code",
            field: "CommodityCodes",
            minWidth: 120,
            tooltipField: "CommodityCodes",
        },
        {
            headerName: "Logistic Company",
            field: "LogisticCompany",
            minWidth: 120,
            tooltipField: "LogisticCompany",
        },
        {
            field: "",
            maxWidth: 80,
            cellRenderer: function (params) {
                return <ActionRenderer selectedData={params.data} handleViewEvent={handleView} />
            },
            sortable: false,
            filter: false,
            pinned: 'right',
            resizable: false
        },
    ];

    const headerName = (field) => {
        let name;
        switch (field) {
            case "ItemCode":
                name = "Item Code";
                break;
            case "ItemDescription":
                name = "Item Description";
                break;
            case "ItemType":
                name = "Item Type";
                break;
            case "ProductClass":
                name = "Product Class";
                break;
            case "CommodityCodes":
                name = "Commodity Code";
                break;
            case "LogisticCompany":
                name = "Logistic Company";
                break;
            default:
                name = "No match found";
                break;
        }
        return name;
    };

    const fieldName = (header) => {
        let name;
        switch (header) {
            case "Item Code":
                name = "ItemCode";
                break;
            case "Item Description":
                name = "ItemDescription";
                break;
            case "Item Type":
                name = "ItemType";
                break;
            case "Product Class":
                name = "ProductClass";
                break;
            case "Commodity Code":
                name = "CommodityCodes";
                break;
            case "Logistic Company":
                name = "LogisticCompany";
                break;
            default:
                name = "No match found";
                break;
        }
        return name;
    };

    const fetchItemMaster = async () => {
        let allResults = [];
        let lastEvaluatedKey = null;
        let batchCount = 0;
        const BATCH_SIZE = 2000;

        setLoading(true);
        dispatch(searchActions.setSearch(""));

        try {
            do {
                batchCount++;
                console.log(`Fetching batch ${batchCount}...`);

                const batch = await getItems(BATCH_SIZE, lastEvaluatedKey);

                // Combine results
                allResults = [...allResults, ...batch.items];
                lastEvaluatedKey = batch.lastEvaluatedKey;

                // Update UI immediately with current batch
                const sortedResults = [...allResults].sort(sortByPropertyInAscending("BodStatus")).sort((a, b) => {
                    if (a.BodStatus === b.BodStatus) {
                        return sortByPropertyInAscending("ItemCode")(a, b);
                    }
                    return 0;
                });
                
                setFilteredResult(sortedResults);
                setExcelData(sortedResults);

                console.log(`Batch ${batchCount} loaded: ${batch.items.length} records (Total: ${allResults.length})`);

                // Small delay to prevent UI blocking
                await new Promise(resolve => setTimeout(resolve, 50));

            } while (lastEvaluatedKey);

            // Final sort after all data loaded
            const finalResults = allResults.sort(sortByPropertyInAscending("BodStatus")).sort((a, b) => {
                if (a.BodStatus === b.BodStatus) {
                    return sortByPropertyInAscending("ItemCode")(a, b);
                }
                return 0;
            });

            setFilteredResult(finalResults);
            setExcelData(finalResults);
            setResetFilter(!resetFilter);

            console.log(`All data loaded: ${allResults.length} total records`);

        } catch (error) {
            console.error("Error fetching item master data:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchItemMaster();
    }, [reload]);

    const handleView = async (row) => {
        setLoading(true);
        let tempUnique = "SK#" + row.SK;
        await addLogDetails(row, "View", tempUnique, "ItemMasterLog", userDetails, "Master");
        setAction("View");
        setRowData(row);
        setShowForm(true);
        setLoading(false);
    }

    const handleGlobalExport = async () => {
        let filename = "Item_Master";
        let excelDownoadData = [];

        excelData.forEach(element => {
            excelDownoadData.push({
                ItemCode: element.ItemCode,
                ItemDescription: element.ItemDescription,
                ItemType: element.ItemType,
                ProductClass: element.ProductClass,
                CommodityCodes: element.CommodityCodes,
                LogisticCompany: element.LogisticCompany,
            });
        });

        let colName = ["Item Code", "Item Description", "Item Type", "Product Class", "Commodity Code", "Logistic Company"];
        let Fieldname = ["ItemCode", "ItemDescription", "ItemType", "ProductClass", "CommodityCodes", "LogisticCompany"];
        exportToExcel(filename, "Data", excelDownoadData, "A1:F1", colName, Fieldname);
    };

    return (
        <>
            {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
            <BreadcrumbComp />
            <Container fluid className="main-container">
                <ActionComp
                    exportEvent={() => handleGlobalExport()} />
                <GridMaster
                    rowDetails={filteredResult}
                    colDetails={columnDefs}
                    fieldNames={fieldName}
                    headerNames={headerName}
                    getDataEvent={(getFilteredData) => setFilteredResulttemp(getFilteredData)}
                    getFilterColLength={(getLength) => setFilterColLength(getLength)}
                    setExcelData={setExcelData}
                    handleView={handleView}
                    resetind={resetFilter}
                />

                {showForm && (
                    <ViewItemMaster
                        show={showForm}
                        action={action}
                        rowdata={rowData}
                        onHide={() => setShowForm(false)}
                        setReload={setReload}
                        reload={reload}
                    />
                )}
            </Container>
        </>
    );
};

export default ItemMaster;
